import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthViewModel } from '../ViewModel/AuthViewModel';
import MFAVerificationPopup from '../Components/MFAVerificationPopup';
import { MFAService } from '../Service/MFAService';
import { Cloud } from '@mui/icons-material';

export const BitbucketCallbackView: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { handleBitbucketCallback, isLoading } = useAuthViewModel();
  const [showMFAPopup, setShowMFAPopup] = useState(false);
  const [mfaData, setMFAData] = useState<{
    email: string;
    qai: string;
    code: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = searchParams.get('code');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setError('Bitbucket authentication was cancelled or failed');
      return;
    }

    if (!code) {
      setError('No authorization code received from Bitbucket');
      return;
    }

    // Handle the callback
    handleBitbucketCallback(code)
      .then((result) => {
        if (result && result.requiresMFA) {
          setMFAData({
            email: result.email,
            qai: result.qai,
            code: result.code
          });
          setShowMFAPopup(true);
        } else {
          // If no MFA required, navigate to dashboard or home
          navigate('/dashboard');
        }
      })
      .catch((error) => {
        console.error('Bitbucket callback error:', error);
        setError(error.message || 'Failed to authenticate with Bitbucket');
      });
  }, [searchParams, handleBitbucketCallback, navigate]);

  const handleMFAVerification = async (token: string) => {
  if (!mfaData) return;
  
  try {
    const response = await MFAService.verifyMFA(
      mfaData.email,
      token,
      mfaData.code,
      "user_type",
      "bitbucket"
    );
    
    // Add token storage ✅
    localStorage.setItem('authToken', `Bearer ${response.access_token}`);
    setShowMFAPopup(false);
    setMFAData(null);
    navigate('/sites'); // Use consistent navigation
    
  } catch (error) {
    throw error;
  }
};

  const handleReturnToLogin = () => {
    navigate('/login');
  };

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
            !
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Authentication Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={handleReturnToLogin}
            className="w-full bg-blue-500 text-white rounded-md p-3 hover:bg-blue-600"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
        <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
          <Cloud className="w-8 h-8" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Authenticating</h2>
        <p className="text-gray-600 mb-6">Processing your Bitbucket authentication...</p>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
      </div>
      
      {showMFAPopup && mfaData && (
        <MFAVerificationPopup
          qrCode={mfaData.qai}
          email={mfaData.email}
          onSubmit={handleMFAVerification}
          onCancel={() => {
            setShowMFAPopup(false);
            setMFAData(null);
            navigate('/login');
          }}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};